/**
 * Line-by-line classification of pasted chart text.
 *
 * The deterministic heuristics are ported from song-fetcher (src/parser.py,
 * transforms/junk_detector.py): section headers, chord-line vs lyric-line,
 * and junk such as tablature, chord diagrams and strumming patterns.
 */
import { isMarker, readChord, readChordLine, readLeadingChords, tokenizeLine } from './chordTokens.js';
import { hasInlineChords, hasInlineParenChords, slotsFromChordLine, slotsFromInlineChords, slotsFromLeadingChords, } from './rowBuilding.js';
import { toAsciiPunctuation } from './textNormalization.js';
import { parseTempoText, parseTimeSignatureText } from './metaText.js';
// ---------------------------------------------------------------------------
// Section headers
// ---------------------------------------------------------------------------
const SECTION_WORDS = [
    'intro(?:duction)?', 'introducci[oó]n', 'introdu[cç][aã]o', 'outro', 'coda', 'tag', 'end(?:ing)?', 'final',
    'verse', 'verso', 'estrofa', 'strofa', 'strophe', 'couplet',
    'pr[eé][- ]?chorus', 'post[- ]?chorus', 'chorus', 'refrain', 'refrein', 'refr[aã]o', 'ritornello',
    'estribillo', 'coro', 'pr[eé][- ]?coro', 'pr[eé][- ]?refr[aã]o', 'pr[eé][- ]?refrain',
    'bridge', 'puente', 'ponte', 'pont', 'brug', 'middle (?:8|eight)',
    'interlude', 'interludio', 'zwischenspiel', 'vorspiel', 'nachspiel', 'instrumental', 'solo',
    'break(?:down)?', 'riff', 'hook', 'vamp', 'turnaround', 'link', 'rap', 'spoken', 'ad[- ]?lib',
    'fade(?: out)?', 'part', 'section',
].join('|');
const SECTION_QUALIFIERS = 'guitar|piano|keys|bass|drum|sax|vocal|instrumental|first|second|third|last|final|short|double';
/** "Verse 2", "PRE-CHORUS", "Guitar Solo (x2)", "Chorus x2", "Estribillo" */
const SECTION_LABEL = new RegExp(`^(?:(?:${SECTION_QUALIFIERS})\\s+)?(?:${SECTION_WORDS})` +
    `(?:\\s*[-#]?\\s*(?:\\d{1,2}|[ivx]{1,4}|[a-d])\\b)?` +
    `(?:\\s*\\([^)]{0,30}\\))?` +
    `(?:\\s*-?\\s*(?:[x×]\\s?\\d{1,2}|\\d{1,2}\\s?[x×]))?$`, 'iu');
const QUOTES = /["“”„‟'‘’‚‛]/g;
/** A section name, ignoring quotes around it: "Verse 2", "“Chorus” – x2". */
const isSectionLabel = (text) => text.length <= 40 && SECTION_LABEL.test(text.replace(QUOTES, '').trim());
/** Title as written, minus double quotes ("“Chorus”"); apostrophes are part of names. */
const sectionTitle = (text) => text.replace(/["“”„‟]/g, '').trim();
/**
 * A header's title plus whatever follows the label on its line: a repeat
 * count joins the title ("Chorus x2"), chords form a row of their own.
 */
const withHeaderRest = (title, rest) => {
    const section = { type: 'section', title: sectionTitle(title) };
    const trimmed = rest.trim();
    const ascii = toAsciiPunctuation(trimmed);
    if (trimmed === '')
        return [section];
    if (isMarker(ascii))
        return [{ type: 'section', title: `${section.title} ${trimmed}` }];
    const tokens = readChordLine(ascii);
    if (tokens)
        return [section, { type: 'slots', slots: slotsFromChordLine(tokens, null) }];
    return [section, ...classifyContent(trimmed, ascii)];
};
const BRACKET_HEADER = /^\s*\[([^\]]{1,40})\]\s*(.*)$/;
const COLON_HEADER = /^\s*(\p{L}[^:[\]{}]{0,29}?)\s*:\s*(.*)$/u;
/**
 * "[Verse 1]", "Chorus:", "INTRO", "Intro: G C D", "Intro  G  C  D".
 * Matching runs on the original line (punctuation conversion keeps every
 * bracket and colon in place), checks on its ASCII form.
 */
const readSectionHeader = (line, ascii) => {
    const bracket = line.match(BRACKET_HEADER);
    if (bracket) {
        const inner = toAsciiPunctuation(bracket[1].trim());
        const rest = toAsciiPunctuation(bracket[2].trim());
        // "[G]", "[N.C.]", "[G C D]" are chords, "[*Riff]" a ChordPro annotation and "[-]" a
        // hold, not headers. "[Verse 1]" stands alone or is followed by a repeat count or chords.
        const isName = /[\p{L}\p{N}]/u.test(inner) && !inner.startsWith('*') && !readChord(inner) && !isMarker(inner) && !readChordLine(inner);
        if (isName && (rest === '' || isMarker(rest) || readChordLine(rest))) {
            return withHeaderRest(bracket[1], bracket[2]);
        }
    }
    const colon = line.match(COLON_HEADER);
    if (colon) {
        const [, label, rest] = colon;
        if (isSectionLabel(toAsciiPunctuation(label)))
            return withHeaderRest(label, rest);
        // "Main groove: G C D" — a short label introducing a row of real chords
        const tokens = readChordLine(toAsciiPunctuation(rest));
        if (tokens?.some(token => token.kind === 'chord') && label.split(/\s+/).length <= 3) {
            return withHeaderRest(label, rest);
        }
    }
    if (isSectionLabel(ascii))
        return [{ type: 'section', title: sectionTitle(line) }];
    // A label then chords, no colon
    const words = [...ascii.matchAll(/\S+/g)];
    for (let n = 1; n <= Math.min(3, words.length - 1); n++) {
        const labelEnd = (words[n - 1].index ?? 0) + words[n - 1][0].length;
        if (isSectionLabel(ascii.slice(0, labelEnd)) && readChordLine(ascii.slice(labelEnd))) {
            return withHeaderRest(line.slice(0, labelEnd), line.slice(labelEnd));
        }
    }
    return null;
};
// ---------------------------------------------------------------------------
// Junk: tablature, chord diagrams, strumming patterns, metadata
// ---------------------------------------------------------------------------
const TAB_BODY = /^[\s|:\-0-9hpbrstvx/\\~*()<>.=^]+$/i;
/** "e|---0---2---|", "|--3--|", "E-----3-----" */
const isTabLine = (ascii) => {
    const body = ascii.replace(/^\s*[A-Ga-g][#b]?(?=\s*[|:-])/, '');
    return TAB_BODY.test(body) && ((body.includes('|') && /-{3,}/.test(body)) || /-{5,}/.test(body));
};
/** "Am  x02210", "C: 032010", "G 3-2-0-0-0-3" — a chord diagram legend */
const isFingeringLine = (ascii) => ascii.split(/[\s:=]+/).some(token => /^[x0-9]{6}$/i.test(token) || /^(?:[x0-9]{1,2}-){5}[x0-9]{1,2}$/i.test(token));
/** "D DU UDU", "↓ ↑ ↓ ↑", "1 & 2 & 3 & 4 &" (uppercase only: "Du du du" is a lyric) */
const isStrummingLine = (ascii) => {
    const trimmed = ascii.trim();
    return (/^[DUx↓↑\s-]+$/.test(trimmed) && /[U↑]/.test(trimmed)) || (/^[\d&+\s.]+$/.test(trimmed) && trimmed.includes('&'));
};
const METADATA = new RegExp('^\\s*(title|song|artist|band|album|by|capo|tuning|key|tempo|bpm|time signature|time|difficulty|' +
    'tabbed by|transcribed by|chords by|author|composer|lyrics by|music by|words and music by|written by|' +
    'strumming(?: pattern)?|strum|chords used|chords|ccli(?: song)?(?: number| #)?|copyright|source|year|genre)' +
    '\\s*[:=]\\s*(.*)$', 'i');
const CAPO_OR_TUNING = /^\s*(?:no\s+capo|capo\b|(?:standard|drop [a-g]|half[- ]step down|open [a-g])\s+tuning\b)/i;
/** A chord-site page heading copied with the chart: "Wonderwall Chords by Oasis" */
const PAGE_HEADING_TITLE = /^\s*(.+?)\s+(?:chords|tabs?|ukulele(?: chords)?|bass(?: tabs?)?|guitar pro|power tab)\s+by\s+\S.*$/i;
/** A tempo or time signature the text states; unreadable values are skipped like other metadata */
const tempoLine = (value) => {
    const tempo = parseTempoText(value);
    return tempo === null ? { type: 'skip' } : { type: 'tempo', value: tempo };
};
const timeLine = (value) => {
    const timeSignature = parseTimeSignatureText(value);
    return timeSignature === null ? { type: 'skip' } : { type: 'time', value: timeSignature };
};
/** `isFirstLine`: a page heading only counts at the top ("…the bass by the door" is a lyric). */
const readMetadata = (ascii, isFirstLine) => {
    const pageTitle = isFirstLine ? ascii.match(PAGE_HEADING_TITLE) : null;
    if (pageTitle)
        return { type: 'title', value: pageTitle[1] };
    const match = ascii.match(METADATA);
    if (match) {
        const field = match[1].toLowerCase();
        const value = match[2].trim();
        if ((field === 'title' || field === 'song') && value)
            return { type: 'title', value };
        if (field === 'key' && value)
            return { type: 'key', value };
        if (field === 'tempo' || field === 'bpm')
            return tempoLine(value);
        if (field === 'time' || field === 'time signature')
            return timeLine(value);
        return { type: 'skip' };
    }
    return CAPO_OR_TUNING.test(ascii) ? { type: 'skip' } : null;
};
// ---------------------------------------------------------------------------
// ChordPro directives
// ---------------------------------------------------------------------------
const DIRECTIVE = /^\{\s*([a-z_][a-z0-9_]*)\s*(?::\s*(.*?))?\s*\}$/i;
const SECTION_SHORTHANDS = { soc: 'chorus', sov: 'verse', sob: 'bridge', sot: 'tab', sog: 'grid' };
const END_SHORTHANDS = { eoc: 'chorus', eov: 'verse', eob: 'bridge', eot: 'tab', eog: 'grid' };
const COMMENTS = new Set(['comment', 'c', 'comment_italic', 'ci', 'comment_box', 'cb', 'highlight']);
/** Blocks whose lines aren't chart content */
const SKIPPED_BLOCKS = new Set(['tab', 'abc', 'ly', 'svg', 'textblock']);
export const isDirective = (line) => DIRECTIVE.test(line.trim());
const capitalize = (name) => name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' ');
const readDirective = (name, value, state) => {
    const directive = name.toLowerCase();
    const start = SECTION_SHORTHANDS[directive] ?? directive.match(/^start_of_(\w+)$/)?.[1];
    const end = END_SHORTHANDS[directive] ?? directive.match(/^end_of_(\w+)$/)?.[1];
    if (directive === 'title' || directive === 't')
        return value ? { type: 'title', value } : { type: 'skip' };
    if (directive === 'key')
        return value ? { type: 'key', value } : { type: 'skip' };
    if (directive === 'tempo')
        return tempoLine(value);
    if (directive === 'time')
        return timeLine(value);
    // Comments are often labels ({c: Chorus}), which start a section; anything else
    // ({c: Softly, guitar only}) is an instruction, kept as the section's note
    if (COMMENTS.has(directive)) {
        if (!value)
            return { type: 'skip' };
        return isSectionLabel(toAsciiPunctuation(value)) ? { type: 'section', title: value } : { type: 'note', text: value };
    }
    // {chorus} recalls the chorus: the last one is played again here
    if (directive === 'chorus')
        return [{ type: 'section', title: value || 'Chorus', recallChorus: true }, { type: 'sectionEnd' }];
    if (start) {
        if (SKIPPED_BLOCKS.has(start)) {
            state.skippedBlock = start;
            return { type: 'skip' };
        }
        if (start === 'grid')
            return value ? { type: 'section', title: value } : { type: 'ignored' };
        return { type: 'section', title: value || capitalize(start), ...(start === 'chorus' && { chorus: true }) };
    }
    if (end)
        return end === 'grid' ? { type: 'ignored' } : { type: 'sectionEnd' };
    // Metadata ({artist}, {capo}, …), chord definitions, and unknown directives
    return { type: 'skip' };
};
// ---------------------------------------------------------------------------
// Lines
// ---------------------------------------------------------------------------
/** Lyrics, or the chords on a line. `line` is the original text, `ascii` the same with ASCII punctuation. */
const classifyContent = (line, ascii) => {
    if (hasInlineChords(ascii))
        return [{ type: 'slots', slots: slotsFromInlineChords(line, 'bracket') }];
    const tokens = readChordLine(ascii);
    if (tokens)
        return [{ type: 'chords', tokens }];
    const leading = readLeadingChords(ascii);
    if (leading)
        return [{ type: 'slots', slots: slotsFromLeadingChords(leading.tokens, line.slice(leading.lyricsIndex)) }];
    if (hasInlineParenChords(ascii))
        return [{ type: 'slots', slots: slotsFromInlineChords(line, 'paren') }];
    return [{ type: 'lyrics', text: line }];
};
const classifyLine = (line, format, state) => {
    if (line.trim() === '')
        return [{ type: 'blank' }];
    const ascii = toAsciiPunctuation(line);
    const isFirstLine = !state.seenContent;
    state.seenContent = true;
    const directive = ascii.trim().match(DIRECTIVE);
    if (state.skippedBlock) {
        const name = directive?.[1].toLowerCase();
        if (name && (END_SHORTHANDS[name] ?? name.match(/^end_of_(\w+)$/)?.[1]) === state.skippedBlock) {
            state.skippedBlock = null;
        }
        return [{ type: 'skip' }];
    }
    if (directive)
        return [readDirective(directive[1], (directive[2] ?? '').trim(), state)].flat();
    if (format === 'chordpro' && ascii.trimStart().startsWith('#'))
        return [{ type: 'skip' }];
    if (isTabLine(ascii) || isFingeringLine(ascii) || isStrummingLine(ascii))
        return [{ type: 'skip' }];
    const metadata = readMetadata(ascii, isFirstLine);
    if (metadata)
        return [metadata];
    const header = readSectionHeader(line, ascii);
    if (header)
        return header;
    // Only bar lines, dots and dashes: "| | |", "........"
    if (tokenizeLine(ascii).length === 0)
        return [{ type: 'skip' }];
    return classifyContent(line, ascii);
};
/** ChordPro if the text has directives or inline [chords]; otherwise chords over lyrics. */
export const detectFormat = (lines) => lines.some(line => isDirective(line) || hasInlineChords(toAsciiPunctuation(line))) ? 'chordpro' : 'chordsOverLyrics';
export const classifyLines = (lines, format) => {
    const state = { skippedBlock: null, seenContent: false };
    return lines.flatMap(line => classifyLine(line, format, state));
};
